








































import {translate} from "@/filters";
import {FoundVacanciesFilter, FoundVacanciesOption} from "@/model/find-vacancies-response";
import {VacancyListFilter} from "@/model/vacancy-list-filter";
import Vue from "vue";
import {Debouncer} from "@/utils/debouncer";
import IntersectObserver from "@/components/intersect-observer/IntersectObserver.vue"

export default Vue.extend({
		name: "VacancySelectFilter",
    components: {
		  "intersect-observer": IntersectObserver
    },
		props: {
			activeFilter: {
				type: Object as () => VacancyListFilter,
				required: true
			},
			filter: {
				type: Object as () => FoundVacanciesFilter,
				required: true
			},
			secondaryFilter: {
				type: Boolean,
				default: false
			},
			singleSelect: {
				type: Boolean,
				default: false
			},
			debouncer: {
				type: Object as () => Debouncer,
				default: new Debouncer()
			}
		},
		data() {
			return <{
				dropdownExpanded: boolean,
				searchOption: string,
        displayableOptions: FoundVacanciesOption[],
        endIndex: number,
        startIndex: number,
        threshold: number,
        margin: number
			}>{
				dropdownExpanded: false,
				searchOption: "",
        displayableOptions: [],
        endIndex: 20,
        startIndex: 0,
        threshold: 0.5,
        margin: 15
			};
		},

    watch: {
      activeFilter() {},
      filter() {},
      searchOption: function(val: string) {
        const me = this;
        this.debouncer.debounce(function() {
          if (val.length < 2) {
            me.displayableOptions = me.filter.options;
            return;
          }

          me.displayableOptions = me.filter.options.filter(option => option.label.toLowerCase().includes(val.toLowerCase()));
          me.startIndex = 0
          me.endIndex = 15
        }, 200);
      },
    },

		mounted(): void {
			this.displayableOptions = this.filter.options;
			this.sortOptionsByState();

			if (this.endIndex > this.displayableOptions.length) {
        this.endIndex =  this.displayableOptions.length;
      }

		},

		computed: {
			selectedCount(): number {
				return this.filter.options.filter(option => option.selected).length;
			},
			selectedLabel(): string {
				if (this.selectedCount === 0) {
					return translate("vueComponents.selectFilter.chooseOption");
				}
				return `${this.selectedCount} ${translate("vueComponents.selectFilter.selected")}`;
			},
			shownOptions(): FoundVacanciesOption[] {
			  return this.displayableOptions.slice(this.startIndex, this.endIndex)
      },

      displayableOptionsLength(): number {
			  return this.displayableOptions.length;
      },

      optionsElement(): Element {
			  return this.$refs.optionsList as Element;
      },

      showSearchInput(): boolean {
			  return this.filter.options.length > 5;
      },

      offsetTop(): number {
			  return this.showSearchInput ? 32 : 0;
      }
		},

		methods: {
			toggleDropdown() {
				this.dropdownExpanded = !this.dropdownExpanded;
			},

			toggleOption(option: FoundVacanciesOption) {
				const currentFilterOptions = this.activeFilter.getFilterOptions(this.filter.name);
				if (currentFilterOptions.includes(option.value)) {
					this.activeFilter.removeFilterOption(this.filter.name, option.value);
				} else {
					this.activeFilter.appendFilterOption(this.filter.name, option.value);
				}
				this.$forceUpdate();
			},

			isOptionSelected(option: FoundVacanciesOption): boolean {
				const draftSelectedOptions = this.activeFilter.getFilterOptions(this.filter.name)
					.find(optionValue => option.value === optionValue);
				return draftSelectedOptions != null;
			},

			sortOptionsByState(){
        const selected = this.displayableOptions.filter((x)=>(x.selected));
        selected.sort((x, y) => (x.label > y.label) ? 1 : -1);
				const noMatch = this.displayableOptions.filter((x)=>(x.remainingCount == 0 && !x.selected));
				noMatch.sort((x, y) => (x.label > y.label) ? 1 : -1);
				const match = this.displayableOptions.filter((x)=>(x.remainingCount > 0 && !x.selected));
				match.sort((x, y) => (x.label > y.label) ? 1 : -1);
        this.displayableOptions = selected.concat(match).concat(noMatch);
			},

			apply() {
				this.$emit("updated");
				this.toggleDropdown();
			},

      loadNextOptions() {
        const reachedEndOfOptions = this.endIndex === this.displayableOptionsLength
        if(reachedEndOfOptions) return

        // getting reference lastOption before updating the list
        const lastOption = this.optionsElement.children[this.optionsElement.children.length - 2]
        const originalLabelFromPreviousLastOption = this.getOriginalLabelForOption(lastOption)

        // update list
        this.determineShownOptions(this.endIndex, this.margin, 0, this.displayableOptionsLength)

        this.$nextTick(() => {
          const element = this.findElementWith(originalLabelFromPreviousLastOption)
          this.alignScrollBottomFrom(element.offsetTop)
        })
      },

      loadPreviousOptions() {

			  const reachedBeginningOfOptions = this.startIndex === 0
			  if (reachedBeginningOfOptions) return;

			  // getting reference firstOption before updating the list
			  const firstOption = this.optionsElement.children[1]
        const originalLabelOfFirstOption = this.getOriginalLabelForOption(firstOption)

        // update list
        this.determineShownOptions(this.startIndex, this.margin, 0, this.displayableOptionsLength)

        this.$nextTick(() => {
          const previousFirstOptions = this.findElementWith(originalLabelOfFirstOption)
          this.alignScrollTopFrom(previousFirstOptions.offsetTop)
        })
      },

      findElementWith(originalLabel: string): HTMLElement {
        const options = Array.from(this.optionsElement.children).filter(e => e.tagName === "LI")

        return options.find(o => {
          const label = o.children[0] as HTMLElement
          return label.dataset.originallabel === originalLabel
        }) as HTMLElement
      },

      getOriginalLabelForOption(listElement: Element): string {
        const label: HTMLElement = listElement.children[0] as HTMLElement
        const originalLabel = label.dataset.originallabel
        return originalLabel ? originalLabel : ""
      },

      alignScrollBottomFrom(top: number) {
        (this.optionsElement as Element).scrollTop = top - (this.optionsElement as HTMLElement).offsetHeight
      },

      alignScrollTopFrom(top: number) {
        (this.optionsElement as Element).scrollTop = top - this.offsetTop
      },

      determineShownOptions(index: number, margin: number, minIndex: number, maxIndex: number) {
        const hypothesizedPreferredMinIndex = index - margin;
        const hypothesizedLowestPossibleIndex = Math.max(hypothesizedPreferredMinIndex, minIndex);
        const differencePreferredPossibleLowest = hypothesizedLowestPossibleIndex - hypothesizedPreferredMinIndex;

        const preferredMaxIndex = index + margin + differencePreferredPossibleLowest;
        const highestPossibleIndex = Math.min(preferredMaxIndex, maxIndex);
        const differencePreferredPossibleMax = highestPossibleIndex - preferredMaxIndex;

        const lowestPreferredPossibleIndex = hypothesizedPreferredMinIndex + differencePreferredPossibleMax;
        const lowestPossibleIndex = Math.max(lowestPreferredPossibleIndex, minIndex);

        this.endIndex = highestPossibleIndex;
        this.startIndex = lowestPossibleIndex;
      }
		}
	});
